/* ═══════════════════════════════════════════════════════════════
   Home page interactions.

   Four small behaviours, no framework. The React build reached for
   Framer Motion (hero slider, hook rotation) and GSAP ScrollTrigger
   (the pinned grill reveal); both are replaced here by an
   IntersectionObserver, a requestAnimationFrame scroll read, and two
   setIntervals. Every one of them degrades to a correct static page
   if the script never loads.

     · hero slider      crossfading backdrop, dots, pause on hover
     · hero hooks       one catchphrase at a time
     · builder teaser   preset chips → live grill quote
     · grill reveal     sticky pin, scroll-scrubbed 3D turn-up
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  /* ── Hero backdrop slider ─────────────────────────────────────── */

  function heroSlider(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gmd-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gmd-dot]'));
    if (slides.length < 2) return;

    var index = 0;
    var paused = false;
    var every = (parseFloat(root.getAttribute('data-interval')) || 5.2) * 1000;

    function show(next) {
      slides[index].classList.remove('is-active');
      if (dots[index]) dots[index].classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      if (dots[index]) {
        dots[index].classList.add('is-active');
        dots[index].setAttribute('aria-selected', 'true');
      }
      dots.forEach(function (d, i) {
        if (i !== index) d.setAttribute('aria-selected', 'false');
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        show(i);
      });
    });

    root.addEventListener('mouseenter', function () {
      paused = true;
    });
    root.addEventListener('mouseleave', function () {
      paused = false;
    });

    if (reduceMotion) return;
    var timer = setInterval(function () {
      if (!paused && !document.hidden) show(index + 1);
    }, every);
    root.gmdTeardown = function () {
      clearInterval(timer);
    };
  }

  /* ── Rotating hero catchphrase ────────────────────────────────── */

  function heroHooks(root) {
    var hooks = Array.prototype.slice.call(root.querySelectorAll('[data-gmd-hook]'));
    if (hooks.length < 2 || reduceMotion) return;

    var i = 0;
    var timer = setInterval(function () {
      hooks[i].classList.remove('is-active');
      i = (i + 1) % hooks.length;
      hooks[i].classList.add('is-active');
    }, 3400);

    var previous = root.gmdTeardown;
    root.gmdTeardown = function () {
      if (previous) previous();
      clearInterval(timer);
    };
  }

  /* ── Builder teaser ───────────────────────────────────────────────
     The same formula the React builder used, so the teaser and the
     real builder never disagree: per-tooth price is a metal base plus
     the silver spot times that metal's sensitivity, all multiplied by
     the finish. The teaser is always 925 Silver / Solid, which makes
     the whole thing a function of teeth count and spot.

     Spot arrives from the ticker section over a 'gmd:spot' event; with
     no ticker on the page the server-rendered seed value stands. */

  var SILVER_BASE = 62;
  var SILVER_SENSITIVITY = 1.15;

  function grillTotal(teeth, spot) {
    var perTooth = Math.round(SILVER_BASE + spot * SILVER_SENSITIVITY);
    var moldFee = teeth >= 6 ? 0 : 25;
    return perTooth * teeth + moldFee;
  }

  function builder(root) {
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-gmd-preset]'));
    var totalEl = root.querySelector('[data-gmd-builder-total]');
    var teethEl = root.querySelector('[data-gmd-builder-teeth]');
    var labelEl = root.querySelector('[data-gmd-builder-label]');
    var noteEl = root.querySelector('[data-gmd-builder-note]');
    var spotEl = root.querySelector('[data-gmd-builder-spot]');

    var spot = parseFloat(root.getAttribute('data-spot')) || 0;
    var teeth = parseInt(root.getAttribute('data-teeth'), 10) || 20;

    function paint() {
      if (totalEl) totalEl.textContent = money.format(grillTotal(teeth, spot));
      if (teethEl) teethEl.textContent = String(teeth);
      if (spotEl) spotEl.textContent = spot.toFixed(2);
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) {
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        teeth = parseInt(chip.getAttribute('data-teeth'), 10) || teeth;
        if (labelEl) labelEl.textContent = chip.getAttribute('data-label') || '';
        if (noteEl) noteEl.textContent = chip.getAttribute('data-note') || '';
        paint();
      });
    });

    document.addEventListener('gmd:spot', function (e) {
      if (!e.detail || e.detail.metal !== 'Silver') return;
      spot = e.detail.price;
      paint();
    });

    paint();
  }

  /* ── Hero live readout ────────────────────────────────────────── */

  function heroSpot(root) {
    var priceEl = root.querySelector('[data-gmd-hero-spot]');
    var deltaEl = root.querySelector('[data-gmd-hero-delta]');
    var setEl = root.querySelector('[data-gmd-hero-set]');
    if (!priceEl && !setEl) return;

    var teeth = parseInt(root.getAttribute('data-set-teeth'), 10) || 28;

    document.addEventListener('gmd:spot', function (e) {
      if (!e.detail || e.detail.metal !== 'Silver') return;
      if (priceEl) priceEl.textContent = '$' + e.detail.price.toFixed(2);
      if (deltaEl) {
        var pct = e.detail.changePct;
        deltaEl.textContent = (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
        deltaEl.style.color = pct >= 0 ? 'var(--c-up)' : 'var(--c-down)';
      }
      if (setEl) setEl.textContent = money.format(grillTotal(teeth, e.detail.price));
    });
  }

  /* ── Pinned grill reveal ──────────────────────────────────────────
     The pin is CSS (a sticky child of a tall track), so this only has
     to turn scroll position into a 0–1 progress value. One rAF-gated
     scroll listener, and it detaches itself while the band is off
     screen so an eight-section page is not paying for it. */

  function grillReveal(track) {
    if (reduceMotion) return;

    var stage = track.querySelector('[data-gmd-grill-stage]');
    var panels = Array.prototype.slice.call(track.querySelectorAll('[data-gmd-grill-panel]'));
    if (!stage) return;

    var ticking = false;

    function frame() {
      ticking = false;
      var box = track.getBoundingClientRect();
      var travel = box.height - window.innerHeight;
      if (travel <= 0) return;

      var p = Math.min(1, Math.max(0, -box.top / travel));
      stage.style.setProperty('--p', p.toFixed(4));

      // The sweep runs once, between 12% and 62% of the scrub.
      var s = (p - 0.12) / 0.5;
      if (s >= 0 && s <= 1) {
        stage.style.setProperty('--sweep', (s * 2 - 1).toFixed(3));
        stage.style.setProperty('--sweep-o', String(1 - Math.abs(s * 2 - 1)));
      } else {
        stage.style.setProperty('--sweep-o', '0');
      }

      // 01 → 02 → 03 as the turn-up completes.
      var step = p < 0.34 ? 0 : p < 0.62 ? 1 : 2;
      panels.forEach(function (panel, i) {
        panel.classList.toggle('is-active', i === step);
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }

    /* One rAF-gated listener, kept attached.

       This used to detach itself whenever the band was off screen,
       which cost nothing and was wrong. An IntersectionObserver only
       calls back when the intersection changes, and jumping from
       below the band to above it is not-intersecting on both sides —
       no crossing, no callback, so the progress stayed wherever it
       was on the way out. Scroll past the grill and back to the top
       and it was already turned up: the reveal spent before anyone
       reached it. The handler is a rect read and a few custom
       properties, once per frame at most. */
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();

    track.gmdTeardown = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  /* ── Wiring ───────────────────────────────────────────────────── */

  var BEHAVIOURS = [
    ['[data-gmd-hero]', function (el) {
      heroSlider(el);
      heroHooks(el);
      heroSpot(el);
    }],
    ['[data-gmd-builder]', builder],
    ['[data-gmd-grill-track]', grillReveal],
  ];

  function init(scope) {
    var root = scope || document;
    BEHAVIOURS.forEach(function (pair) {
      root.querySelectorAll(pair[0]).forEach(function (el) {
        if (el.dataset.gmdReady) return;
        el.dataset.gmdReady = '1';
        pair[1](el);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-gmd-ready]').forEach(function (el) {
      if (el.gmdTeardown) el.gmdTeardown();
    });
  });
})();
